
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';

export default function ContactForm() {
  const { t } = useTranslation('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('message', formData.message);

      const response = await fetch(formElement.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-[#F5F7FA] py-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0D1B2A] mb-4">
            {t('form.title')}
          </h2>
          <p className="text-lg text-[#394B59] max-w-2xl mx-auto">
            {t('form.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12">
          <form 
            id="contact_form"
            data-readdy-form
            action="https://readdy.ai/api/form/d49fhcv43pgcqhvcq2d0"
            method="POST"
            onSubmit={handleSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#0D1B2A] mb-2">
                  {t('form.name')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8BC0] focus:border-transparent outline-none transition-all duration-300 text-sm"
                  placeholder={t('form.namePlaceholder')}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#0D1B2A] mb-2">
                  {t('form.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8BC0] focus:border-transparent outline-none transition-all duration-300 text-sm"
                  placeholder={t('form.emailPlaceholder')}
                />
              </div>
            </div>

            {/* Company */}
            <div className="mb-6">
              <label htmlFor="company" className="block text-sm font-semibold text-[#0D1B2A] mb-2">
                {t('form.company')}
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8BC0] focus:border-transparent outline-none transition-all duration-300 text-sm"
                placeholder={t('form.companyPlaceholder')}
              />
            </div>

            {/* Message */}
            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-[#0D1B2A] mb-2">
                {t('form.message')} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E8BC0] focus:border-transparent outline-none transition-all duration-300 resize-none text-sm"
                placeholder={t('form.messagePlaceholder')}
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">
                {formData.message.length}/500 {t('form.charactersRemaining')}
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col items-center">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="w-full md:w-auto px-12 py-3 text-lg"
              >
                {isSubmitting ? t('form.sending') : t('form.submit')}
              </Button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-center">
                  <i className="ri-check-line text-xl mr-2"></i>
                  {t('form.successMessage')}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
                  <i className="ri-error-warning-line text-xl mr-2"></i>
                  {t('form.errorMessage')}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
